import React from "react";
import { TONES } from "../../../shared/tones";
import { VERDINT_TINTS } from "../constants";
import { Sentence } from "../../kit";

// ─── Shared: VerdictBanner ──────────────────────────────────────────────────────
// WI-style verdict card for the Work page. A full-width tinted glass panel with a
// briefcase icon, "Work Insight" title, the one-sentence verdict (`verdict.says`),
// an optional Focus line + "See items →" action link, and a top-right dismiss X.
// The tint comes from VERDINT_TINTS (mapped off `verdict.tint`, falling back to
// the neutral blue/indigo `gray` tone). Returns null when there's no verdict and
// we're not loading, so the page hides the block entirely (no empty panel /
// spacing). Uses an inline Briefcase glyph — no extra deps.
//
// Props:
//   verdict      — { says, tint, focus, generated_at } | null
//   isLoading    — true while the fn_verdict RPC is in flight
//   onDismiss    — called by the X button; page flips a session-only dismissed flag
//   onFocusClick — (legacy) called by the "See items →" link; retained for
//                  backward compatibility but the link now drills via openDrill
//   openDrill    — called by the "See items →" link as
//                  openDrill(verdict.focus.what, verdict.focus.id) to open the
//                  kit <DrillSheet>. When verdict.focus is null the link is hidden.
const VerdictBanner = ({ verdict, isLoading, onDismiss, onFocusClick, openDrill }) => {
  // Loading skeleton — full-width rounded container with animate-pulse bars for
  // the title and body. Shown only while the RPC is in flight and no verdict has
  // arrived yet. Neutral container (the tint comes from the verdict data, which
  // we don't have yet).
  if (isLoading && !verdict) {
    return (
      <div
        role="region"
        aria-label="Work summary"
        aria-busy="true"
        className="relative w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-4 animate-pulse"
      >
        <div className="flex items-start gap-3">
          <div className="w-5 h-5 rounded bg-gray-200 dark:bg-gray-700 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="h-4 w-28 bg-gray-200 dark:bg-gray-700 rounded mb-2" />
            <div className="h-4 w-3/4 bg-gray-200 dark:bg-gray-700 rounded" />
          </div>
        </div>
      </div>
    );
  }

  // No verdict and not loading → hide entirely (no spacing).
  if (!verdict) return null;

  const tone = VERDINT_TINTS[verdict.tint] || TONES.gray;

  return (
    <div
      role="region"
      aria-label="Work summary"
      className={`relative w-full ${tone.bg} border ${tone.border} rounded-lg p-4`}
    >
      {/* Dismiss X — top-right corner */}
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss work insight"
        className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
      >
        <svg
          className="w-4 h-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>

      {/* Header row: briefcase icon + content */}
      <div className="flex items-start gap-3">
        <svg
          className={`w-5 h-5 ${tone.icon} flex-shrink-0 mt-0.5`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="2" y="7" width="20" height="14" rx="2" />
          <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        </svg>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">
            Work Insight
          </h3>
          <Sentence
            says={verdict.says}
            tint={verdict.tint}
            className="text-gray-800 dark:text-gray-100"
          />

          {/* Focus section — "See items →" action link opens the kit DrillSheet.
              verdict.focus is { what, id } | string | null. When it's null the
              whole section is hidden (link included). The muted "Focus:" label is
              only shown for the legacy string shape; the object shape carries no
              display text (the RPC shape for focus.text is UNCONFIRMED), so we
              don't guess a field name. The link is shown when openDrill exists. */}
          {verdict.focus && (
            <div className="mt-1">
              {typeof verdict.focus === "string" && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Focus: {verdict.focus}
                </p>
              )}
              {openDrill && (
                <button
                  type="button"
                  onClick={() => openDrill(verdict.focus.what, verdict.focus.id)}
                  className="mt-1 inline-flex items-center gap-0.5 text-xs font-medium text-[#32B1FF] hover:underline"
                >
                  See items
                  <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerdictBanner;
